import { useCallback, useEffect, type RefObject } from 'react';

function waitForPrintRender(): Promise<void> {
  return new Promise(resolve => {
    // Two frames gives React a chance to commit prepared print state before the browser opens print preview.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
}

async function waitForPrintImages(root: HTMLElement | null): Promise<void> {
  if (!root) return;

  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(image => {
      if (image.complete && image.naturalWidth > 0) return Promise.resolve();

      if (typeof image.decode === 'function') {
        // Print preview can capture blank boxes if data-URL images have not decoded yet.
        return image.decode().catch(() => undefined);
      }

      return new Promise<void>(resolve => {
        image.addEventListener('load', () => resolve(), { once: true });
        image.addEventListener('error', () => resolve(), { once: true });
      });
    })
  );
}

export type UsePreparedPrintOptions = {
  rootRef: RefObject<HTMLElement | null>;
  prepare?: () => Promise<void> | void;
  errorMessage?: string;
};

export function usePreparedPrint({ rootRef, prepare, errorMessage = 'Unable to prepare this page for printing. Please try again.' }: UsePreparedPrintOptions) {
  const print = useCallback(async () => {
    try {
      await prepare?.();
      await waitForPrintRender();
      await waitForPrintImages(rootRef.current);
      window.print();
    } catch (err) {
      console.error('Print Error:', err);
      alert(errorMessage);
    }
  }, [errorMessage, prepare, rootRef]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'p') return;

      // Native Ctrl/Cmd+P would run before charts are snapshotted into the print portal.
      event.preventDefault();
      void print();
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [print]);

  return print;
}
